import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Star, Sparkles } from 'lucide-react';

const emptyForm = { name: '', price_monthly: 0, price_yearly: 0, max_products: -1, max_orders: -1, max_customers: -1, features: [], is_active: true, description: '', cta_label: '', is_popular: false, is_default_trial: false };

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [featuresCatalog, setFeaturesCatalog] = useState([]);

  const fetchPackages = async () => {
    const res = await api.get('/admin/packages');
    setPackages(res.data.packages);
  };
  useEffect(() => {
    fetchPackages();
    api.get('/features').then((res) => setFeaturesCatalog(res.data.features || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (editing) {
      await api.put(`/admin/packages/${editing}`, form);
    } else {
      await api.post('/admin/packages', form);
    }
    setShowDialog(false); setEditing(null);
    setForm(emptyForm);
    fetchPackages();
  };

  const edit = (pkg) => {
    setEditing(pkg.id);
    setForm({
      name: pkg.name,
      price_monthly: pkg.price_monthly,
      price_yearly: pkg.price_yearly,
      max_products: pkg.max_products,
      max_orders: pkg.max_orders,
      max_customers: pkg.max_customers,
      features: Array.isArray(pkg.features) ? pkg.features : [],
      is_active: pkg.is_active,
      description: pkg.description || '',
      cta_label: pkg.cta_label || '',
      is_popular: !!pkg.is_popular,
      is_default_trial: !!pkg.is_default_trial,
    });
    setShowDialog(true);
  };

  const toggleFeature = (code) => {
    setForm((p) => {
      const has = p.features.includes(code);
      return { ...p, features: has ? p.features.filter((c) => c !== code) : [...p.features, code] };
    });
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v);

  // Map code → label for displaying chips on the cards
  const labelByCode = Object.fromEntries(featuresCatalog.map((f) => [f.code, f.label]));

  // Group catalog by category
  const grouped = featuresCatalog.reduce((acc, f) => {
    const cat = f.category || 'Lainnya';
    (acc[cat] = acc[cat] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="admin-packages-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Paket SaaS</h1>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-package-btn">
          <Plus size={16} className="mr-2" /> Tambah Paket
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packages.map((pkg, i) => (
          <Card key={pkg.id} className={`border-slate-200 shadow-sm ${!pkg.is_active ? 'opacity-60' : ''} ${pkg.is_popular ? 'ring-2 ring-[#2D6CDF]/50' : ''}`} data-testid={`package-card-${i}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                  {pkg.is_popular && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2D6CDF] text-white text-[10px] font-bold uppercase">
                      <Star size={10} className="fill-current" /> Populer
                    </span>
                  )}
                  {pkg.is_default_trial && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase border border-emerald-300">
                      <Sparkles size={10} /> Default Trial
                    </span>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => edit(pkg)} data-testid={`edit-package-${pkg.id}`}><Edit size={14} /></Button>
              </div>
              {pkg.description && (
                <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <p className="text-3xl font-bold text-[#2D6CDF]">Rp {fmt(pkg.price_monthly)}<span className="text-sm text-slate-500 font-normal">/bulan</span></p>
                <p className="text-sm text-slate-500">Rp {fmt(pkg.price_yearly)}/tahun</p>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <p>Produk: {pkg.max_products === -1 ? 'Unlimited' : pkg.max_products}</p>
                <p>Order: {pkg.max_orders === -1 ? 'Unlimited' : pkg.max_orders}</p>
                <p>Customer: {pkg.max_customers === -1 ? 'Unlimited' : pkg.max_customers}</p>
              </div>
              {(pkg.features || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {(pkg.features || []).map((f, j) => (
                    <span key={j} className="px-2 py-0.5 bg-[#2D6CDF]/10 text-[#2D6CDF] rounded text-xs font-medium">
                      {labelByCode[f] || f}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Paket' : 'Tambah Paket'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Paket</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Starter, Business, Enterprise" data-testid="package-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Harga Bulanan (Rp)</Label>
                <Input type="number" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Harga Tahunan (Rp)</Label>
                <Input type="number" value={form.price_yearly} onChange={(e) => setForm({ ...form, price_yearly: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Max Produk</Label>
                <Input type="number" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: parseInt(e.target.value) || -1 })} />
                <p className="text-[11px] text-slate-500 mt-1">-1 = Unlimited</p>
              </div>
              <div>
                <Label>Max Order</Label>
                <Input type="number" value={form.max_orders} onChange={(e) => setForm({ ...form, max_orders: parseInt(e.target.value) || -1 })} />
                <p className="text-[11px] text-slate-500 mt-1">-1 = Unlimited</p>
              </div>
              <div>
                <Label>Max Customer</Label>
                <Input type="number" value={form.max_customers} onChange={(e) => setForm({ ...form, max_customers: parseInt(e.target.value) || -1 })} />
                <p className="text-[11px] text-slate-500 mt-1">-1 = Unlimited</p>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <Label className="text-base font-semibold">Tampilan di Landing Page</Label>
              <p className="text-xs text-slate-500 mb-3">Teks ini muncul di kartu pricing halaman beranda Tokomoe.</p>
              <div className="space-y-3">
                <div>
                  <Label>Deskripsi Paket</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Contoh: Cocok untuk pemula yang baru mulai bisnis online"
                    rows={2}
                    data-testid="package-description-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Teks Tombol (CTA)</Label>
                    <Input
                      value={form.cta_label}
                      onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                      placeholder="Contoh: Mulai Sekarang / Coba Sekarang / Hubungi Sales"
                      data-testid="package-cta-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Kalau dikosongkan, otomatis pakai default sesuai nama paket.</p>
                  </div>
                  <div>
                    <Label>Tandai sebagai "Populer"</Label>
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={!!form.is_popular}
                        onCheckedChange={(v) => setForm({ ...form, is_popular: v })}
                        data-testid="package-popular-toggle"
                      />
                      <span className="text-sm text-slate-600">{form.is_popular ? 'Ya, tampilkan badge "Populer"' : 'Tidak'}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Disarankan satu paket saja. Boleh lebih, semua akan dapat badge.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="flex-1">
                  <Label className="text-base font-semibold flex items-center gap-1.5">
                    <Sparkles size={15} className="text-emerald-600" /> Jadikan Paket Default Free Trial
                  </Label>
                  <p className="text-xs text-slate-600 mt-1">
                    Saat tenant baru mendaftar, mereka otomatis dapat paket ini. Fitur yang tampil di sidebar tenant akan mengikuti yang dicentang di atas. <strong>Hanya satu paket</strong> yang bisa jadi default — paket lain otomatis di-unset.
                  </p>
                </div>
                <Switch
                  checked={!!form.is_default_trial}
                  onCheckedChange={(v) => setForm({ ...form, is_default_trial: v })}
                  data-testid="package-default-trial-toggle"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <Label className="text-base font-semibold">Fitur yang Termasuk</Label>
              <p className="text-xs text-slate-500 mb-3">Centang fitur yang dapat diakses tenant pada paket ini. Fitur bertanda <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">MODUL</span> akan diblokir saat tenant tidak memilikinya.</p>
              {featuresCatalog.length === 0 ? (
                <p className="text-sm text-slate-400">Memuat daftar fitur...</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{cat}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {items.map((f) => {
                          const checked = form.features.includes(f.code);
                          return (
                            <label
                              key={f.code}
                              className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFeature(f.code)}
                                className="mt-0.5 accent-[#2D6CDF]"
                                data-testid={`feature-${f.code}`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-medium text-slate-900">{f.label}</span>
                                  {f.is_module && (
                                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">MODUL</span>
                                  )}
                                </div>
                                {f.description && <p className="text-xs text-slate-500">{f.description}</p>}
                                <p className="text-[10px] text-slate-400 font-mono mt-0.5">{f.code}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-package-btn">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
